import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTopButton from '@/components/base/BackToTopButton';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { allMovies, categoryMap, slugToCategory } from '@/mocks/movies';
import { useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? slugToCategory[slug] : undefined;
  const categoryName = category ? categoryMap[category] : '分类';

  const categoryMovies = category
    ? allMovies.filter((m) => m.category === category)
    : [];

  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');
  const sortedMovies = [...categoryMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return 0;
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 午马影院`,
    description: `午马影院${categoryName}频道，高清不缓冲免费在线观看最新${categoryName}，海量资源即点即播。`,
    url: `https://wumacinema.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '午马影院',
      url: 'https://wumacinema.com',
    },
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="text-foreground-500">分类不存在</p>
            <Link to="/" className="mt-3 inline-block text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName },
              ]}
            />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {categoryMovies.length} 部{categoryName}资源，高清不缓冲免费在线观看
              </p>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <span className="text-xs text-foreground-500">排序：</span>
              {[
                { key: 'default' as const, label: '默认' },
                { key: 'rating' as const, label: '评分最高' },
                { key: 'year' as const, label: '最新发布' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4 pb-12">
              {sortedMovies.map((movie, idx) => (
                <div key={movie.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.04}s` }}>
                  <MovieCard movie={movie} index={idx} />
                </div>
              ))}
            </div>

            {sortedMovies.length === 0 && (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </span>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
        <BackToTopButton />
      </div>
    </>
  );
}